import { useMemo } from 'react';
import { Building2, GraduationCap, UsersRound, type LucideIcon } from 'lucide-react';
import { hasAcademyManagementRole, hasClubManagementRole, hasRole, TierFeatureKey } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import { useCurrentAcademy } from '@/hooks/useDataStore';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';

export type DashboardModeSwitchRole =
  | 'academy_owner'
  | 'academy_admin'
  | 'club_owner'
  | 'club_admin'
  | 'guardian'
  | 'admin'
  | 'coach';

export interface DashboardModeSwitchModel {
  show: boolean;
  activeSide: 'left' | 'right';
  leftPath: string;
  rightPath: string;
  leftTooltip: string;
  rightTooltip: string;
  LeftIcon: LucideIcon;
  RightIcon: LucideIcon;
}

/**
 * Shared rules for switching between academy/guardian context and coach dashboard (see DashboardSidebar).
 */
export function useDashboardModeSwitch(
  dashboardRole: DashboardModeSwitchRole | undefined,
  academySlug: string | undefined
): DashboardModeSwitchModel | null {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const { hasAccess: hasCoachPortalAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.COACH_PORTAL
  );
  const { hasAccess: hasBrandedCoachDashboardAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.BRANDED_DASHBOARDS
  );

  return useMemo(() => {
    if (!user || !dashboardRole) return null;

    const hasGuardianAccess = hasRole(user, 'guardian');
    const hasCoachAccess = hasRole(user, 'coach');
    const hasAcademyManagementAccess = hasAcademyManagementRole(user);

    const isManagementDashboardRole =
      dashboardRole === 'academy_owner' ||
      dashboardRole === 'academy_admin' ||
      dashboardRole === 'club_owner' ||
      dashboardRole === 'club_admin';

    if (!hasCoachPortalAccess) return null;

    const ownerToCoachAcademySlug = academySlug ?? currentAcademy?.slug;

    const canSwitchOwnerToCoach =
      isManagementDashboardRole && hasCoachAccess && !!ownerToCoachAcademySlug;

    const canSwitchCoachToOwner =
      dashboardRole === 'coach' &&
      (hasAcademyManagementAccess || hasClubManagementRole(user));

    const canSwitchCoachToGuardian =
      dashboardRole === 'coach' && hasGuardianAccess && !!academySlug;

    const canSwitchGuardianToCoach =
      dashboardRole === 'guardian' &&
      hasCoachAccess &&
      !!academySlug &&
      hasBrandedCoachDashboardAccess;

    const show =
      canSwitchOwnerToCoach ||
      canSwitchCoachToOwner ||
      canSwitchCoachToGuardian ||
      canSwitchGuardianToCoach;

    if (!show || !ownerToCoachAcademySlug) return null;

    const rightPath = `/academy/${ownerToCoachAcademySlug}/coach/dashboard`;

    let leftPath: string;
    let leftTooltip: string;
    let LeftIcon: LucideIcon;

    if (canSwitchCoachToOwner || canSwitchOwnerToCoach) {
      leftPath = '/admin/dashboard';
      leftTooltip = 'Academy management dashboard';
      LeftIcon = Building2;
    } else if (canSwitchCoachToGuardian || canSwitchGuardianToCoach) {
      if (!academySlug) return null;
      leftPath = `/academy/${academySlug}/dashboard`;
      leftTooltip = 'Student & family (guardian) dashboard';
      LeftIcon = UsersRound;
    } else {
      return null;
    }

    const activeSide: 'left' | 'right' = dashboardRole === 'coach' ? 'right' : 'left';

    return {
      show: true,
      activeSide,
      leftPath,
      rightPath,
      leftTooltip,
      rightTooltip: 'Coach dashboard',
      LeftIcon,
      RightIcon: GraduationCap,
    };
  }, [
    user,
    dashboardRole,
    academySlug,
    currentAcademy?.slug,
    hasCoachPortalAccess,
    hasBrandedCoachDashboardAccess,
  ]);
}
